import React, { useEffect, useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Info } from 'lucide-react';

import { createTanstackTable } from '../../common/utils';
import { useGetSentInvitations } from '../../hooks/tanstack/useSentInvitations';
import {
  useGetRouteParams,
  useGetSearchResults,
} from '../../hooks/tanstack/useUtilsHook';
import TanstackTable from '../TanstackTable';

import SentInvitationDialog from './SentInvitationDialog';

export type DataRow = {
  id: number;
  receiver: {
    name?: string;
    email: string;
  };
  sender: {
    name?: string;
    email: string;
  };
  project: string;
  model: string;
  acceptedAt: string;
  createdAt: string;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = [
  columnHelper.accessor('receiver', {
    cell: (info) => {
      const { name, email } = info.getValue();

      return (
        <div className="group flex flex-col items-start text-start">
          <p className="font-semibold">{name ?? email?.split('@')?.[0]}</p>
          <p className="text-[#666] text-sm">{email}</p>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-2">
        <p>Receiver</p>
      </div>
    ),
  }),
  columnHelper.accessor('project', {
    id: 'project',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">{info.getValue()}</div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      ),
    header: () => <div>Project</div>,
  }),
  columnHelper.accessor('model', {
    id: 'model',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">{info.getValue()}</div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      ),
    header: () => <div>Model</div>,
  }),
  columnHelper.accessor('sender', {
    cell: (info) => {
      const { name, email } = info.getValue();

      return (
        <div className="group flex flex-col items-start">
          <p className="font-semibold">{name ?? 'Unknown'}</p>
          <p className="text-[#666]">{email}</p>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-2">
        <p>Sender</p>
      </div>
    ),
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">
          {format(info.getValue() as string, 'y/MM/dd h:mm a')}
        </div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      ),
    sortingFn: (a, b) =>
      new Date(a.original.createdAt as string).getTime() -
      new Date(b.original.createdAt as string).getTime(),
    header: () => <div>Date Requested</div>,
  }),
  columnHelper.accessor('acceptedAt', {
    id: 'acceptedAt',
    cell: (info) => {
      const [open, setOpen] = useState(false);

      const { orgId } = useGetRouteParams();

      const value = info.getValue();
      const projectName = info.row.original.project;

      const { data: sentInvitations } = useGetSentInvitations(
        { orgId },
        {
          enabled: !!orgId,
        }
      );

      const handleOpen = () => {
        setOpen(true);
      };

      const handleClose = () => {
        setOpen(false);
      };

      const invitationId = info.row.original.id;

      const invitation = sentInvitations?.find((r) => r.id === invitationId);

      return (
        <div className="flex justify-between gap-5">
          {value ? (
            <div className="w-full">
              {format(value as string, 'y/MM/dd h:mm a')}
            </div>
          ) : (
            <div className="text-gray-400 w-full text-center">--</div>
          )}
          <Info color="#2196F3" onClick={handleOpen} />
          <SentInvitationDialog
            open={open}
            handleOnClose={handleClose}
            data={{
              name: invitation?.model.name ?? '',
              projectName,
              description: invitation?.model.description ?? '',
              from:
                invitation?.shared_by.first_name +
                ' ' +
                invitation?.shared_by.last_name,
              to: invitation?.email ?? '',
            }}
          />
        </div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.acceptedAt as string).getTime() -
      new Date(b.original.acceptedAt as string).getTime(),
    header: () => <div>Date Accepted</div>,
    size: 200,
  }),
];

const SentInvitationsTable = ({ searchKey }: { searchKey: string }) => {
  const [data, setData] = useState<DataRow[]>([]);

  const { orgId } = useGetRouteParams();

  const {
    data: sentInvitations,
    isLoading: isFetchingSentInvitations,
    isRefetching: isRefetchingSentInvitation,
  } = useGetSentInvitations(
    { orgId },
    {
      enabled: !!orgId,
    }
  );

  const searchResults = useGetSearchResults<DataRow>({
    searchValue: searchKey,
    data,
    filterFields: [
      'receiver.name',
      'receiver.email',
      'project',
      'model',
      'sender.email',
      'sender.name',
    ],
  });

  useEffect(() => {
    if (sentInvitations) {
      setData(
        sentInvitations.map((r) => ({
          id: r.id,
          receiver: {
            name: r.receiver.full_name,
            email: r.receiver.email,
          },
          project: r.project.name,
          model: r.model.name,
          acceptedAt: r.updated_at,
          createdAt: r.created_at,
          sender: {
            name: r.shared_by.first_name + ' ' + r.shared_by.last_name,
            email: r.shared_by.email,
          },
        })) as DataRow[]
      );
    }
  }, [sentInvitations]);

  const table = createTanstackTable<DataRow>({
    data: searchResults ?? data,
    columns,
    defaultSorting: {
      id: 'createdAt',
      desc: true,
    },
  });

  const isLoading = isRefetchingSentInvitation || isFetchingSentInvitations;

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      emptyState={searchKey ? 'No results' : 'No available sent invitations.'}
    />
  );
};

export default SentInvitationsTable;
